import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Nav,
  Tab,
  Spinner,
  OverlayTrigger,
  Tooltip,
  ButtonGroup,
  ListGroup,
  InputGroup
} from 'react-bootstrap';
import {
  FaShieldAlt,
  FaUser,
  FaUsers,
  FaLock,
  FaUnlock,
  FaEdit,
  FaSave,
  FaTrash,
  FaPlus,
  FaSearch,
  FaCheck,
  FaTimes,
  FaBriefcase,
  FaKey,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

interface Screen {
  id: number;
  screen_name: string;
  screen_path: string;
  screen_category: string;
  is_active: boolean;
  icon_name?: string;
  parent_screen_id?: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  user_type: 'agent' | 'broker' | 'admin';
  subscription_package_name: string;
  brokerage_name?: string;
}

interface AccessLevel {
  screen_id: number;
  access_level: 'none' | 'view' | 'edit' | 'full';
  granted_by?: string;
  reason?: string;
}

interface ScreenCategory {
  category: string;
  screens: Screen[];
  expanded: boolean;
}

const RoleBasedAccessControl: React.FC = () => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userAccess, setUserAccess] = useState<Map<number, AccessLevel>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [editMode, setEditMode] = useState(false);
  const [screenCategories, setScreenCategories] = useState<ScreenCategory[]>([]);

  // Group screens by category
  const groupScreensByCategory = (screenList: Screen[]): ScreenCategory[] => {
    const categories = new Map<string, Screen[]>();
    
    screenList.forEach(screen => {
      const category = screen.screen_category || 'Other';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(screen);
    });

    return Array.from(categories.entries()).map(([category, screens]) => ({
      category,
      screens: screens.sort((a, b) => a.screen_name.localeCompare(b.screen_name)),
      expanded: true
    }));
  };

  // Fetch all screens from the registry
  const fetchScreens = async () => {
    try {
      const response = await axiosInstance.get('/admin/screens');
      console.log('Fetched screens:', response.data);
      setScreens(response.data.screens || []);
      setScreenCategories(groupScreensByCategory(response.data.screens || []));
    } catch (error: any) {
      console.error('Error fetching screens:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You need admin privileges to view this page.');
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else {
        toast.error(`Failed to load screens: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  // Fetch users (agents and brokers)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/users', {
        params: { include_agents: true }
      });
      console.log('Fetched users:', response.data);
      setUsers(response.data.users || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You need admin privileges to view this page.');
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else {
        toast.error(`Failed to load users: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's current screen access
  const fetchUserAccess = async (userId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/users/${userId}/screen-access`);
      console.log('Fetched user access:', response.data);
      
      const accessMap = new Map<number, AccessLevel>();
      response.data.access.forEach((access: AccessLevel) => {
        accessMap.set(access.screen_id, access);
      });
      
      setUserAccess(accessMap);
    } catch (error) {
      console.error('Error fetching user access:', error);
      toast.error('Failed to load user access');
    } finally {
      setLoading(false);
    }
  };

  // Save user's screen access
  const saveUserAccess = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      const accessList = Array.from(userAccess.entries()).map(([screen_id, access]) => ({
        screen_id,
        ...access
      }));

      await axiosInstance.put(`/admin/users/${selectedUser.id}/screen-access`, {
        access: accessList
      });

      toast.success('Screen access updated successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Error saving user access:', error);
      toast.error('Failed to save screen access');
    } finally {
      setSaving(false);
    }
  };

  // Toggle screen access
  const toggleScreenAccess = (screenId: number, currentLevel: string) => {
    if (!editMode) return;

    const levels = ['none', 'view', 'edit', 'full'];
    const currentIndex = levels.indexOf(currentLevel || 'none');
    const nextIndex = (currentIndex + 1) % levels.length;
    const nextLevel = levels[nextIndex] as AccessLevel['access_level'];

    const newAccess = new Map(userAccess);
    newAccess.set(screenId, {
      screen_id: screenId,
      access_level: nextLevel
    });
    
    setUserAccess(newAccess);
  };

  // Get access level badge color
  const getAccessBadgeVariant = (level: string) => {
    switch (level) {
      case 'full': return 'success';
      case 'edit': return 'primary';
      case 'view': return 'info';
      case 'none': return 'secondary';
      default: return 'secondary';
    }
  };

  useEffect(() => {
    fetchScreens();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserAccess(selectedUser.id);
    }
  }, [selectedUser]);

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0">
              <FaShieldAlt className="me-2" />
              Role-Based Access Control
            </h4>
            <small>Manage screen permissions for agents and brokers</small>
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowAddUserModal(true)}
          >
            <FaPlus className="me-2" />
            Add User Permissions
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        <Row>
          {/* User Selection Panel */}
          <Col md={4} className="border-end">
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <ListGroup>
                  {filteredUsers.map(user => (
                    <ListGroup.Item
                      key={user.id}
                      action
                      active={selectedUser?.id === user.id}
                      onClick={() => setSelectedUser(user)}
                      className="cursor-pointer"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold">{user.name}</div>
                          <small className="text-muted">{user.email}</small>
                        </div>
                        <div className="text-end">
                          <Badge bg={user.user_type === 'broker' ? 'warning' : 'info'}>
                            {user.user_type}
                          </Badge>
                          <br />
                          <small className="text-muted">{user.subscription_package_name}</small>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Col>

          {/* Screen Access Configuration */}
          <Col md={8}>
            {selectedUser ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">
                      <FaUser className="me-2" />
                      {selectedUser.name}
                    </h5>
                    <small className="text-muted">
                      {selectedUser.email} • {selectedUser.subscription_package_name}
                    </small>
                  </div>
                  <ButtonGroup>
                    {!editMode ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setEditMode(true)}
                      >
                        <FaEdit className="me-2" />
                        Edit Access
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={saveUserAccess}
                          disabled={saving}
                        >
                          {saving ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setEditMode(false);
                            fetchUserAccess(selectedUser.id);
                          }}
                        >
                          <FaTimes className="me-2" />
                          Cancel
                        </Button>
                      </>
                    )}
                  </ButtonGroup>
                </div>

                {selectedUser.user_type === 'agent' && (
                  <Alert variant="info" className="mb-3">
                    <FaInfoCircle className="me-2" />
                    <strong>Agent Restrictions:</strong> Agents should not have access to 
                    Broker Intelligence Hub, Company Profile, or administrative features.
                  </Alert>
                )}

                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {screenCategories.map(category => (
                    <Card key={category.category} className="mb-3">
                      <Card.Header className="py-2 bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 text-capitalize">
                            {category.category.replace('_', ' ')} Screens
                          </h6>
                          <Badge bg="secondary">{category.screens.length}</Badge>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table hover className="mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: '40%' }}>Screen</th>
                              <th style={{ width: '30%' }}>Path</th>
                              <th style={{ width: '30%' }}>Access Level</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.screens.map(screen => {
                              const access = userAccess.get(screen.id);
                              const accessLevel = access?.access_level || 'none';
                              const isRestricted = selectedUser.user_type === 'agent' && 
                                ['broker-intelligence', 'company-profile', 'broker-portal'].includes(screen.screen_path);
                              
                              return (
                                <tr key={screen.id} className={isRestricted ? 'table-warning' : ''}>
                                  <td>
                                    {screen.screen_name}
                                    {isRestricted && (
                                      <OverlayTrigger
                                        placement="right"
                                        overlay={
                                          <Tooltip>
                                            Restricted for agents
                                          </Tooltip>
                                        }
                                      >
                                        <FaExclamationTriangle 
                                          className="ms-2 text-warning" 
                                        />
                                      </OverlayTrigger>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">{screen.screen_path}</small>
                                  </td>
                                  <td>
                                    <Badge
                                      bg={getAccessBadgeVariant(accessLevel)}
                                      className={editMode ? 'cursor-pointer' : ''}
                                      onClick={() => toggleScreenAccess(screen.id, accessLevel)}
                                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                                    >
                                      {accessLevel === 'none' && <FaLock className="me-1" />}
                                      {accessLevel === 'view' && <FaEye className="me-1" />}
                                      {accessLevel === 'edit' && <FaEdit className="me-1" />}
                                      {accessLevel === 'full' && <FaKey className="me-1" />}
                                      {accessLevel.toUpperCase()}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <FaUsers className="text-muted" style={{ fontSize: '3rem' }} />
                <p className="mt-3 text-muted">Select a user to manage their screen access</p>
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default RoleBasedAccessControl;